import { useState, forwardRef, useImperativeHandle, useRef } from "react";
import LoaderIcon from "./LoaderIcon";
import { API_URL } from "../../utilities/constants";
import { IoCloudUploadOutline } from "react-icons/io5";
import { FcApproval, FcHighPriority } from "react-icons/fc";
import { MdCancel } from "react-icons/md";
import { useUI } from "../../context/ui";

const DragFileUpload = forwardRef(
  ({ afterUpload, fileType, error = false, setTouched = () => {} }, ref) => {
    const [loading, setLoading] = useState(false);
    const [uploadedError, setUploadedError] = useState(null);
    const [f, setF] = useState("");
    const [uploaded, setUploaded] = useState(false);
    const { addToastMessage } = useUI();
    const inputRef = useRef();

    const resetFile = () => {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setF("");
      setUploaded(false);
      setLoading(false);
      afterUpload("");
      setTouched(true);
    };

    useImperativeHandle(ref, () => ({
      resetFile,
    }));

    return (
      <>
        <div
          className={`file-drag-upload flex center-center pos-rel cursor-pointer ${
            error || uploadedError ? "error-border" : ""
          }`}
        >
          <input
            type="file"
            ref={inputRef}
            onBlur={setTouched}
            onChange={async (e) => {
              setUploadedError(null);
              const acceptedFileTypes = [
                // Images
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/webp",
                // PDF
                "application/pdf",
              ];

              if (e.target.files.length > 0) {
                setF(e.target.files[0].name);
                setLoading(true);
                const file = e.target.files[0];
                if (!acceptedFileTypes.includes(file.type)) {
                  setUploadedError(
                    "Invalid file type. Please upload a PNG, JPEG, JPG, WEBP, or PDF file."
                  );
                  setLoading(false);
                  return;
                }

                try {
                  const formdata = new FormData();
                  formdata.append("file", e.target.files[0]);
                  formdata.append("type", fileType);
                  const resp = await fetch(API_URL + `/api/1.0.0/uploadFile`, {
                    method: "POST",
                    headers: {
                      "X-JWT-Assertion": localStorage.getItem("token"),
                    },
                    body: formdata,
                    redirect: "follow",
                  });
                  if (resp.ok) {
                    const response = await resp.json();
                    if (response?.result?.responseCode === 200) {
                      const uploadedURL = response.data;
                      afterUpload(uploadedURL);
                      setUploaded(true);
                    }
                  } else {
                    const response = await resp.json();
                    if (inputRef.current) {
                      inputRef.current.value = "";
                    }
                    setUploadedError(
                      response?.result?.responseDescription ||
                        "File upload failed. Please try again."
                    );
                    addToastMessage({
                      status: false,
							        message: response?.result?.responseDescription || "File upload failed. Please try again.",
                    });
                  }
                } catch (e) {
                  setUploadedError("File upload failed. Please try again.");
                } finally {
                  setLoading(false);
                }
              }
            }}
          />
          <div className="t-a-c">
            <div style={{ marginBottom: "10px" }}>
              {loading ? (
                <LoaderIcon
                  style={{
                    height: "24px",
                    width: "24px",
                  }}
                />
              ) : (
                <>
                  {uploaded ? (
                    <FcApproval />
                  ) : uploadedError ? (
                    <FcHighPriority />
                  ) : (
                    <IoCloudUploadOutline />
                  )}
                </>
              )}
            </div>
            <div>
              {loading ? (
                <p style={{ color: "#8f8f8f" }} className="small l-h-1 ">
                  Uploading...
                  <br />
                </p>
              ) : (
                <>
                  {uploaded ? (
                    <p style={{ color: "#27ae60" }} className="small l-h-1">
                      Your file has been successfully uploaded.
                    </p>
                  ) : uploadedError ? (
                    <p style={{ color: "red" }} className="small l-h-1">
                      {uploadedError}
                    </p>
                  ) : (
                    <p style={{ color: "#8f8f8f" }} className="small l-h-1">
                      Drag files here or click to select files.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {uploaded && (
          <div
            className="flex"
            style={{
              marginTop: "12px",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#f8f9fa",
              padding: "8px 12px",
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease",
            }}
            title={f}
          >
            <span
              style={{
                fontSize: "14px",
                display: "flex",
                maxWidth: "200px",
                whiteSpace: "nowrap",
                alignItems: "center",
                color: "#495057",
                fontWeight: "500",
              }}
            >
              <p
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  margin: 0,
                  paddingRight: "8px",
                }}
              >
                {f}
              </p>
            </span>
            <button
              onClick={resetFile}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#dc3545",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                height: "28px",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "rgba(220, 53, 69, 0.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              title="Remove file"
            >
              <MdCancel style={{ fontSize: "16px" }} />
              Remove
            </button>
          </div>
        )}
      </>
    );
  }
);

export default DragFileUpload;
